// React
// MUI
import { Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useGridApiRef } from "@mui/x-data-grid-premium";

// Hooks
import useSummaryTransformedRows from "@DataPages/hooks/inventory/reconciliation/useSummaryTransformedRows";

// Components
import TypographyCell from "@Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";

// Helpers
import { isFooterCell } from "@Common/helpers/Tables";
import { getCellClasses } from "@Layouts/components/Tables/DataSmartTable/helpers";

// Constants
import { propertiesMap } from "@DataPages/components/Inventory/Tables/Reconciliation/SummaryTable/constants";

// Types
import { ISummaryDetail } from "@DataPages/types/inventory/models/reconciliation";
import { TReconciliationSummaryPropertyRow } from "@DataPages/types/inventory/commons.ts";

type NewVehicleSummaryDetail = Pick<
	ISummaryDetail,
	| "physicalInventoryOnLot"
	| "physicalInventoryOffLot"
	| "unitsInPhysicalInventoryNotOnMfg"
	| "unitsOffPhysicalInventoryNotOnMfg"
	| "perManufacturerReport"
	| "totalManufacture"
	| "differenceManufacture"
>;

type NewVehicleInformationTableProps = Readonly<{
	data: NewVehicleSummaryDetail;
}>;

const propertyRows: TReconciliationSummaryPropertyRow<NewVehicleSummaryDetail>[] = [
	{
		label: "physicalInventoryOnLot",
		idx: 1,
	},
	{
		label: "physicalInventoryOffLot",
		idx: 2,
	},
	{
		label: "unitsInPhysicalInventoryNotOnMfg",
		idx: 3,
	},
	{
		label: "unitsOffPhysicalInventoryNotOnMfg",
		idx: 4,
	},
	{
		label: "totalManufacture",
		idx: 5,
	},
	{
		label: "perManufacturerReport",
		idx: 6,
	},
	{
		label: "differenceManufacture",
		idx: 7,
		isFooter: true,
	},
];

const columns: GridColDef[] = [
	{
		field: "_id",
		headerName: "",
	},
	{
		field: "key",
		headerName: "",
		sortable: false,
		flex: 3,
		cellClassName: (params) =>
			getCellClasses(
				"",
				{
					noWrap: true,
					isFooter: params.row?.isFooter ?? isFooterCell(params) ?? false,
					isSubTotalRow: params.row?.key === propertiesMap["totalManufacture"],
				},
				params
			),
		renderCell: (params) => {
			if (isFooterCell(params)) {
				return (
					<TypographyCell params={params} fontFamily={"Montserrat"}>
						Difference (Should Equal Zero):
					</TypographyCell>
				);
			}
			return (
				<TypographyCell
					params={params}
					fontWeight={params.row?.key === propertiesMap["totalManufacture"] || params.row?.isFooter ? "500" : "700"}
					fontSize={params.row?.key === propertiesMap["totalManufacture"] || params.row?.isFooter ? "14px" : "12px"}
					hints={{
						isFooter: params.row?.isFooter ?? false,
						isSubTotalRow: params.row?.key === propertiesMap["totalManufacture"],
					}}
				/>
			);
		},
	},
	{
		field: "value",
		headerName: "",
		flex: 1,
		align: "right",
		type: "number",
		sortable: false,
		renderCell: (params) => {
			if (isFooterCell(params)) {
				return (
					<TypographyCell params={params} fontFamily={"Montserrat"}>
						{params.value}
					</TypographyCell>
				);
			}
			return (
				<TypographyCell
					params={params}
					hints={{ isSubTotalRow: params.row?.key === propertiesMap["totalManufacture"], isFooter: params.row?.isFooter }}
				/>
			);
		},
		cellClassName: (params) =>
			getCellClasses(
				"",
				{
					noWrap: true,
					isFooter: params.row?.isFooter ?? isFooterCell(params) ?? false,
					isSubTotalRow: params.row?.key === propertiesMap["totalManufacture"],
				},
				params
			),
	},
];
export default function NewVehicleInformationTable(props: NewVehicleInformationTableProps) {
	const apiRef = useGridApiRef();

	const newSummaryTableTransformedRows = useSummaryTransformedRows({
		propertyRows,
		data: props.data,
	});

	return (
		<Stack component={"article"} mb={2.5}>
			<DataSmartTable
				apiRef={apiRef}
				rowHeight={25}
				headerHeight={20}
				rows={newSummaryTableTransformedRows}
				columns={columns}
				disableColumnMenu
				disableRowSelectionOnClick
				columnVisibilityModel={{
					_id: false,
				}}
				customProps={{
					tableClassName: {
						"functional-table footer-table-included": true,
					},
				}}
			/>
		</Stack>
	);
}
